import React from 'react';

import { useNavigate, useParams, useResolvedPath } from 'react-router-dom';

import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { getAssociateProfileQueryOptions } from 'src/services/associateProfile/associateProfileQueries';
import { useAppSelector } from 'src/store/store';

import ActivePlacementCard from './activePlacementCard';
import PendingPlacementCard from './pendingPlacementCard';
import TransferredInPlacementCard from './transferredInPlacementCard';

const AssociateProfilePlacements = () => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);

  const placementsQuery = useQuery(getAssociateProfileQueryOptions.placements(alias ?? '', selectedSite.id));

  if (placementsQuery.isPending) {
    return <LoadingSpinner />;
  }

  if (placementsQuery.isError) {
    return <Text>Oops! Something went wrong.</Text>;
  }

  const placements = placementsQuery.data;

  if (placements.pending.length === 0 && placements.active.length === 0 && placements.transferredIn.length === 0) {
    return <NoPlacements alias={alias!} siteId={selectedSite.id} siteName={selectedSite.site} />;
  }

  return (
    <LoadingSpinnerWrapper showSpinner={placementsQuery.isFetching}>
      <Column spacing="400">
        <Text type="b500">
          Placements Pending Action ({placements.pending.length + placements.transferredIn.length})
        </Text>
        {placements.pending.map((placement) => (
          <PendingPlacementCard key={placement.accommodationId} placement={placement} />
        ))}
        {placements.transferredIn.map((placement) => (
          <TransferredInPlacementCard key={placement.accommodationId} placement={placement} />
        ))}
        <Text type="b500">Active Placements ({placements.active.length})</Text>
        {placements.active.map((placement) => (
          <ActivePlacementCard key={placement.accommodationId} placement={placement} />
        ))}
      </Column>
    </LoadingSpinnerWrapper>
  );
};

const NoPlacements = ({ alias, siteId, siteName }: { alias: string; siteId: number; siteName: string }) => {
  const navigate = useNavigate();
  const pathObj = useResolvedPath('..');

  const lastPlacementDateQuery = useQuery(getAssociateProfileQueryOptions.lastPlacementDate(alias, siteId));
  const lastPlacementDate = lastPlacementDateQuery.data ?? null;

  return (
    <Column>
      <Text>There are no placements active or pending action at site: {siteName}</Text>
      {lastPlacementDate !== null && (
        <Row>
          <Text>
            Last placement closed on <strong>{moment(lastPlacementDate).format('L')}</strong>.
          </Text>
          <Link type="secondary" href={`${pathObj.pathname}/history`} onClick={navigate}>
            View History
          </Link>
        </Row>
      )}
    </Column>
  );
};

export default AssociateProfilePlacements;
